/**
 * What to show an operator for each way the run can decline to proceed (F7).
 *
 * The solver already writes a sentence for every refusal, and that sentence is
 * always shown -- it carries the measured numbers and it is the authority.
 * What this file adds is the part a message cannot: a short title that can be
 * read across a workshop, and *what to do*, which is different for every code
 * even when the message sounds similar.
 *
 * The reshoot/stop distinction is the one that matters and it comes from the
 * refusal itself, never from this table. A blurred photograph is thirty
 * seconds of an operator's time; a mesh-count mismatch means the wrong canvas
 * is on the bed and shooting it again wastes the afternoon.
 */
import type { Refusal, RefusalAction } from './model';

export interface RefusalCopy {
  title: string;
  /** Concrete steps, in the order to try them. */
  steps: string[];
}

const COPY: Record<string, RefusalCopy> = {
  PHOTO_TOO_COARSE: {
    title: 'Too far away',
    steps: [
      'Move closer: the canvas and both registration bars should just fit in '
        + 'the frame.',
      'Hold the phone square to the bed rather than at an angle.',
      'If it still refuses, the canvas may be finer than the design expects.',
    ],
  },
  PHOTO_BLURRED: {
    title: 'Not sharp enough',
    steps: [
      'Tap the canvas on screen to focus, wait for it to settle, then shoot.',
      'Brace your elbows on the machine, or rest the phone on something.',
      'Turn on more light rather than moving closer.',
    ],
  },
  PHOTO_GLARE: {
    title: 'Reflection on the canvas',
    steps: [
      'Switch off or move the light directly above the bed, then shoot again.',
      'Or tilt the phone slightly so the bright patch moves off the canvas. '
        + 'Only slightly: both bars still need to look the same length.',
      'A matte backing sheet under the canvas stops this for good. A glossy '
        + 'one shines up through the holes.',
    ],
  },
  PHOTO_UNUSABLE: {
    title: 'Photo cannot be read',
    steps: [
      'Take another photo with the phone camera rather than a screenshot.',
      'Do not crop, rotate or edit it before uploading.',
    ],
  },
  MESH_NOT_DETECTED: {
    title: 'Weave could not be read',
    steps: [
      'Check for glare: a bright reflection hides the holes underneath it.',
      'Move any light source off the axis between the phone and the bed.',
      'Make sure the whole taped sheet is in frame.',
    ],
  },
  MESH_COUNT_MISMATCH: {
    title: 'Wrong canvas for this design',
    steps: [
      'Check the mesh count printed on the canvas against the one on the job.',
      'If the canvas is right, the job was created with the wrong mesh count. '
        + 'Fix the job rather than the canvas.',
      'Do not print. The stitches would not line up with the holes and the '
        + 'sheet would be scrap.',
    ],
  },
  HARMONIC_LOCK: {
    title: 'Weave measured wrongly',
    steps: [
      'Take another photo slightly closer, filling the frame with the bed.',
      'Every second thread was counted instead of every thread. A tighter '
        + 'frame gives it more to work with and normally fixes it.',
      'Check the mesh count on the job is the one printed on the canvas.',
    ],
  },
  BARS_NOT_FOUND: {
    title: 'Registration bars not visible',
    steps: [
      'Check both bars are fully in frame, front and back.',
      'Move anything resting on or over a bar.',
      'Check for glare across a bar.',
    ],
  },
  MARKERS_NOT_FOUND: {
    title: 'Marker stickers do not match this printer',
    steps: [
      'Check all eight stickers are in frame, with nothing resting on them.',
      'Check for glare across a bar. Moving the light, or your own shadow, '
        + 'is usually enough.',
      'The message says which stickers are missing. Numbers 0 to 3 are on one '
        + 'bar and 4 to 7 on the other, so it points at an end of the bed.',
      'If a bar has been knocked or replaced, print the calibration sheet '
        + 'from the Printers screen and photograph it again.',
    ],
  },
  SURVEY_STALE: {
    title: 'This printer needs measuring again',
    steps: [
      'Run the calibration print from the Printers screen.',
      'Photograph it on the bed and upload it there.',
      'Nothing can be printed on this machine until that is done.',
    ],
  },
  SURVEY_FAILED: {
    title: 'Calibration photo could not be read',
    steps: [
      'Check the calibration sheet actually printed, and every cross is on it.',
      'Leave it where it was printed and photograph it square to the bed.',
      'Both registration bars fully in frame, no glare across the sheet.',
    ],
  },
  LENS_FAILED: {
    title: 'The phone\'s lens could not be measured',
    steps: [
      'Check the lens board printed in full and is held flat by the vacuum.',
      'Use the same phone at 1x for every shot, and keep the board sharp.',
      'Take the close-ups with the board filling the whole screen: the '
        + 'corners of the picture have to be measured.',
    ],
  },
  LENS_NOT_COVERED: {
    title: 'Part of the photo is outside the measured lens',
    steps: [
      'Photograph it again framed a little more loosely: both bars in, '
        + 'with a little space between them and the edges of the screen.',
      'If it keeps happening, measure the lens again from the Printers '
        + 'screen, moving the board over every corner and edge of the screen.',
    ],
  },
  CALIBRATION_INVALID: {
    title: 'This printer has not been set up',
    steps: [
      'Open the Printers screen and check the bar length, width and '
        + 'stand-off are filled in and saved.',
      'Then print the calibration sheet from that screen, leave it where it '
        + 'printed, and photograph it on the bed.',
      'Everything else about this sheet was still measured. The overlays '
        + 'below are worth looking at even though there is no print file.',
    ],
  },
  LATTICE_CONFLICTS: {
    title: 'Weave could not be counted consistently',
    steps: [
      'Almost always glare or a corner too soft to read. Take another photo.',
      'Even out the light across the whole bed, and keep your own shadow off '
        + 'it.',
      'Check the tape has not lifted and is not casting a shadow.',
    ],
  },
  LATTICE_RESIDUAL: {
    title: 'Part of the sheet was read wrongly',
    steps: [
      'Take another photo, square to the bed and evenly lit.',
      'Check the canvas is flat and the tape has not pulled it out of shape.',
    ],
  },
  BRIDGE_TOO_LONG: {
    title: 'Too many holes missing in a row',
    steps: [
      'Usually glare, a shadow, or something resting on the canvas.',
      'Take another photo with the obstruction removed.',
    ],
  },
  COVERAGE_INCOMPLETE: {
    title: 'Design does not fit the bare canvas',
    steps: [
      'There is too much tape overlap. Re-tape with less over the canvas.',
      'The design needs every stitch on bare mesh, not under tape.',
    ],
  },
  PLACEMENT_FAILED: {
    title: 'Design will not fit',
    steps: [
      'Compare the design size with the bare canvas size in the message above.',
      'If the tape covers canvas the design needs, re-tape with less overlap '
        + 'and photograph again.',
      'Otherwise the canvas is too small for this design.',
    ],
  },
  CANVAS_OBSTRUCTED: {
    title: 'Part of the canvas could not be used',
    steps: [
      'The design fits this canvas. Nothing in the design file needs changing.',
      'Something inside the taped edge is in the way: tape reaching over the '
        + 'canvas, uneven light or glare read as tape, or something resting on it.',
      'Re-tape if the tape covers canvas the design needs. Otherwise check the '
        + 'canvas is clear and evenly lit, then photograph it again.',
    ],
  },
  DESIGN_MIRRORED: {
    title: 'Design would print back to front',
    steps: [
      'Do not print. The sheet would come out as a mirror image.',
      'This is a fault in the software, not in your photograph, the canvas '
        + 'or the tape. Another photograph will not fix it.',
      'Stop and report it, and say which job and sheet it was.',
    ],
  },
  ARTWORK_OFF_PAGE: {
    title: 'Canvas is not where the printer thinks it is',
    steps: [
      'Check the sheet is taped square and centred on the bed.',
      'If it is, a registration bar has moved: run the calibration print again.',
      'Do not print: the sheet would be clipped.',
    ],
  },
  ARTWORK_UNSUPPORTED: {
    title: 'Design file cannot be used',
    steps: [
      'The design PDF contains something this software cannot read. The '
        + 'message above says what.',
      'It has to be exported again from the design program. Another photo '
        + 'will not help, and nothing on the bed is wrong.',
    ],
  },
  ARTWORK_MESH_MISMATCH: {
    title: 'Design does not match the job',
    steps: [
      'Check the mesh count on the job against the one the design was drawn '
        + 'for.',
      'If the job is wrong, create it again with the right mesh count. If '
        + 'the job is right, the design has to be exported again at that mesh.',
      'Nothing on the bed is wrong, and another photo will not help. Do not '
        + 'print.',
    ],
  },
  ARTWORK_STRAY_INK: {
    title: 'Stray marks in the design',
    steps: [
      'The design file has a few stitches away from the main design, such as '
        + 'marks at its corners or edges, or an outline. The message above says '
        + 'how big the design is with and without them.',
      'If they are not meant to be stitched, delete them in the design program '
        + 'and export it again. Nothing on the bed is wrong.',
      'Do not print.',
    ],
  },
  TAPE_MISSING: {
    title: 'Canvas is not taped',
    steps: [
      'Tape all four edges of the canvas with the masking tape from the kit.',
      'Then take the photo again. The printable area is found from the tape, '
        + 'so an untaped sheet cannot be placed.',
    ],
  },
  TAPE_NOT_DETECTED: {
    title: 'Tape and canvas look the same',
    steps: [
      'Take another photo with more even light across the whole bed.',
      'The edge of the tape has to be visible against the mesh. If they are '
        + 'close in colour, use the narrower tape from the kit instead.',
    ],
  },
  GATES_FAILED: {
    title: 'Did not pass its checks',
    steps: [
      'Take another photo, square to the bed and evenly lit.',
      'If it refuses a second time, stop and report it. This refusal means '
        + 'the failing check could not be named, which should not happen.',
    ],
  },
  INTERNAL_ERROR: {
    title: 'Something went wrong on our side',
    steps: [
      'Take another photo and try again.',
      'If it happens twice, stop and tell us rather than working around it.',
    ],
  },
};

const FALLBACK: RefusalCopy = {
  title: 'Cannot proceed',
  steps: ['Read the message above; it carries the measured numbers.'],
};

export function refusalCopy(refusal: Refusal): RefusalCopy {
  return COPY[refusal.code] ?? FALLBACK;
}

/** What the operator is being asked to do, in two words. */
export function actionLabel(action: RefusalAction): string {
  return action === 'reshoot' ? 'Take another photo' : 'Stop and fix this';
}

/** Every code this UI has copy for -- used by the tests, not by the screens. */
export const KNOWN_CODES = Object.keys(COPY);
